import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RotateCcw, Save } from 'lucide-react';
import { api } from '../../utils/api';

// Settings → Scoring. Reworked from the old FocusScoreConfig page: the
// tenant names their score, weighs the collected variables (negative
// weights penalise a signal) and sets the firmographic parameters. The
// preview ranking now recomputes live as the sliders move instead of
// waiting for a "Preview ranking" click — calls are debounced so a drag
// across the slider doesn't hammer the endpoint.

const STROKE = 2.75;
const PREVIEW_LIMIT = 8;
const PREVIEW_DEBOUNCE_MS = 450;

function WeightBadge({ value }) {
  const cls = value === 0 ? 'tag tag-neutral' : value > 0 ? 'tag tag-accent-2' : 'tag tag-accent';
  return <span className={`${cls} o-weight-badge`}>{value > 0 ? `+${value}` : value}</span>;
}

function ChipSelect({ kicker, options, selected, onToggle, emptyHint }) {
  return (
    <div className="o-param-panel">
      <span className="card-kicker">{kicker}</span>
      {options.length ? (
        <div className="o-chip-row">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              className={selected.includes(opt) ? 'tag tag-accent-2' : 'tag tag-outline'}
              aria-pressed={selected.includes(opt)}
              onClick={() => onToggle(opt)}
            >
              {opt}
            </button>
          ))}
        </div>
      ) : (
        <span style={{ fontSize: 13, opacity: 0.65 }}>{emptyHint}</span>
      )}
    </div>
  );
}

function ScoringTab() {
  const [catalog, setCatalog] = useState(null);
  const [scoreName, setScoreName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [weights, setWeights] = useState({});
  const [params, setParams] = useState({});
  const [hasConfig, setHasConfig] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(false);
  const previewSeq = useRef(0);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await api.getFocusScoreConfig();
        setCatalog(data);
        if (data.config) {
          setScoreName(data.config.score_name || '');
          setEnabled(data.config.enabled);
          setWeights(data.config.weights || {});
          setParams(data.config.params || {});
          setHasConfig(true);
        }
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const payload = useMemo(() => ({
    score_name: scoreName,
    enabled,
    weights,
    params,
  }), [scoreName, enabled, weights, params]);

  const activeWeights = Object.values(weights).filter((w) => w !== 0).length;
  const showPreview = enabled && activeWeights > 0;

  // Live preview — recomputed from current data as the controls change.
  useEffect(() => {
    if (loading) return undefined;
    if (!showPreview) {
      setPreview(null);
      setPreviewing(false);
      return undefined;
    }
    const seq = ++previewSeq.current;
    setPreviewing(true);
    const handle = setTimeout(async () => {
      try {
        const result = await api.previewFocusScore(payload, PREVIEW_LIMIT);
        if (previewSeq.current === seq) {
          setPreview(result);
          setError(null);
        }
      } catch (err) {
        if (previewSeq.current === seq) setError(err.message);
      } finally {
        if (previewSeq.current === seq) setPreviewing(false);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [payload, showPreview, loading]);

  const setWeight = (key, value) => {
    setWeights((prev) => ({ ...prev, [key]: value }));
    setNotice(null);
  };

  const toggleParamValue = (key, value) => {
    setParams((prev) => {
      const current = prev[key] || [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [key]: next };
    });
    setNotice(null);
  };

  const setYearParam = (key, raw) => {
    setParams((prev) => {
      const next = { ...prev };
      if (raw === '' || raw == null) delete next[key];
      else next[key] = Number(raw);
      return next;
    });
    setNotice(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const resp = await api.saveFocusScoreConfig(payload);
      setHasConfig(true);
      setScoreName(resp.config.score_name);
      setWeights(resp.config.weights || {});
      setParams(resp.config.params || {});
      setNotice(
        resp.config.enabled && Object.keys(resp.config.weights || {}).length
          ? `Saved — Movers now ranks by “${resp.config.score_name}”.`
          : 'Saved — custom score is inactive, Movers stays on momentum.'
      );
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = useCallback(async () => {
    setConfirmRevert(false);
    if (!hasConfig) {
      // Nothing saved yet — just zero the draft weights.
      setWeights((prev) => Object.fromEntries(Object.keys(prev).map((k) => [k, 0])));
      return;
    }
    try {
      await api.deleteFocusScoreConfig();
      setHasConfig(false);
      setScoreName('');
      setEnabled(true);
      setWeights({});
      setParams({});
      setPreview(null);
      setNotice('Custom score removed — Movers is back on momentum.');
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [hasConfig]);

  if (loading) {
    return (
      <div className="card" style={{ padding: 'var(--space-4)', flexDirection: 'row', alignItems: 'center', gap: 8, fontSize: 14, opacity: 0.75 }}>
        <Loader2 size={16} strokeWidth={STROKE} className="spin" /> Loading scoring configuration…
      </div>
    );
  }
  if (error && !catalog) {
    return (
      <div className="card" style={{ padding: 'var(--space-4)', color: 'var(--color-accent-700)' }}>
        Error: {error}
      </div>
    );
  }

  const min = catalog?.weight_min ?? -10;
  const max = catalog?.weight_max ?? 10;
  const options = catalog?.options || { categories: [], countries: [] };

  const name = scoreName.trim() || catalog?.default_score_name || 'your score';

  const activeNote = !enabled
    ? 'This score is saved but switched off, so Movers stays on plain momentum.'
    : activeWeights === 0
      ? 'Nothing is weighted, so Movers stays on plain momentum.'
      : `${activeWeights} variable${activeWeights === 1 ? '' : 's'} weighted. Negative weights penalise a signal — useful to down-rank retail-mature brands.`;

  const noPreviewNote = !enabled
    ? 'This score is switched off, so there is nothing to preview. Tick “Rank Movers by this score” to see the queue it would produce.'
    : 'Every weight is at zero, so there is nothing to rank. Move a slider to see the queue this score would produce.';

  // The raw 0-100 composite, exactly as Movers shows it. Normalising the
  // leader to 100 here made the same brand read differently on the two
  // screens and hid how far off a full-fit 100 the whole queue was.
  const items = preview?.items || [];

  const renderParamInputs = (variable) => {
    const wants = variable.params || [];
    if (!wants.length) return null;
    return (
      <>
        {wants.includes('target_categories') && (
          <ChipSelect
            kicker="Target sub-segments"
            options={options.categories}
            selected={params.target_categories || []}
            onToggle={(v) => toggleParamValue('target_categories', v)}
            emptyHint="No categories yet — add brands first."
          />
        )}
        {wants.includes('target_countries') && (
          <ChipSelect
            kicker="Target countries"
            options={options.countries}
            selected={params.target_countries || []}
            onToggle={(v) => toggleParamValue('target_countries', v)}
            emptyHint="No countries on file yet — brands without HQ data can't be geo-matched."
          />
        )}
        {wants.includes('founded_year_min') && (
          <div className="o-param-panel-row">
            <span className="card-kicker" style={{ margin: 0 }}>Founded between</span>
            <input
              className="input"
              type="number"
              style={{ width: 110 }}
              placeholder="e.g. 2018"
              value={params.founded_year_min ?? ''}
              onChange={(e) => setYearParam('founded_year_min', e.target.value)}
            />
            <span style={{ fontSize: 13 }}>and</span>
            <input
              className="input"
              type="number"
              style={{ width: 110 }}
              placeholder="e.g. 2026"
              value={params.founded_year_max ?? ''}
              onChange={(e) => setYearParam('founded_year_max', e.target.value)}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {error && (
        <div className="card" style={{ padding: 'var(--space-3)', color: 'var(--color-accent-700)', fontSize: 14 }}>
          Error: {error}
        </div>
      )}

      <div className="card" style={{ padding: 'var(--space-4)', gap: 'var(--space-4)' }}>
        <div>
          <h4 className="o-card-head-title">Score identity</h4>
          <p className="o-card-head-sub">
            Build your own fit score from what the platform collects per brand.
            Disable it and Movers goes back to plain momentum.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="field" style={{ minWidth: 280, flex: '1 1 280px' }}>
            <label htmlFor="score-name">Score name, shown on Movers</label>
            <input
              id="score-name"
              className="input"
              type="text"
              maxLength={100}
              placeholder={`e.g. ${catalog?.default_score_name || 'Fit Score'}`}
              value={scoreName}
              onChange={(e) => { setScoreName(e.target.value); setNotice(null); }}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 14, cursor: 'pointer', paddingBottom: 10 }}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => { setEnabled(e.target.checked); setNotice(null); }}
            />
            Rank Movers by this score
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 14, cursor: enabled ? 'pointer' : 'default', paddingBottom: 10, opacity: enabled ? 1 : 0.5 }}>
            <input
              type="checkbox"
              disabled={!enabled}
              checked={!!params.movers_include_flat}
              onChange={(e) => {
                const on = e.target.checked;
                setParams((prev) => {
                  const next = { ...prev };
                  if (on) next.movers_include_flat = true;
                  else delete next.movers_include_flat;
                  return next;
                });
                setNotice(null);
              }}
            />
            Also show top brands that didn’t grow this week
          </label>
        </div>
      </div>

      {(catalog?.variables || []).map((group) => (
        <div className="card" key={group.key} style={{ padding: 'var(--space-4)', gap: 'var(--space-3)' }}>
          <div>
            <h4 className="o-card-head-title">{group.label}</h4>
            {group.description && <p className="o-card-head-sub">{group.description}</p>}
          </div>
          {group.variables.map((variable) => {
            const value = weights[variable.key] ?? 0;
            const paramInputs = renderParamInputs(variable);
            return (
              <div key={variable.key} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                <div className="o-weight-row">
                  <div className="o-weight-label">
                    <strong>{variable.label}</strong>
                    <div className="o-weight-desc">{variable.description}</div>
                  </div>
                  <div className="o-weight-controls">
                    <input
                      type="range"
                      min={min}
                      max={max}
                      step={1}
                      value={value}
                      aria-label={`Weight for ${variable.label}`}
                      onChange={(e) => setWeight(variable.key, Number(e.target.value))}
                    />
                    <WeightBadge value={value} />
                  </div>
                </div>
                {paramInputs}
              </div>
            );
          })}
        </div>
      ))}

      <div className="card" style={{ padding: 'var(--space-4)', flexDirection: 'row', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={15} strokeWidth={STROKE} /> {saving ? 'Saving…' : 'Save configuration'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => setConfirmRevert(true)}>
          <RotateCcw size={15} strokeWidth={STROKE} />
          <span style={{ whiteSpace: 'nowrap' }}>Revert to momentum</span>
        </button>
        <span style={{ fontSize: 13, opacity: 0.65, flex: '1 1 260px', textWrap: 'pretty' }}>
          {notice || activeNote}
        </span>
      </div>

      <div className="card" style={{ padding: 'var(--space-4)', gap: 'var(--space-3)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <div>
            <h4 className="o-card-head-title">Preview — top {PREVIEW_LIMIT} by {name}</h4>
            <p className="o-card-head-sub">
              Recomputed from current data as you move the sliders. Nothing is saved yet.
            </p>
          </div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {previewing && <Loader2 size={14} strokeWidth={STROKE} className="spin" aria-label="Recomputing preview" />}
            <span className="tag tag-accent">{activeWeights} weighted</span>
          </span>
        </div>

        {showPreview ? (
          items.length ? (
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 56 }}>#</th>
                  <th>Brand</th>
                  <th>Category</th>
                  <th>Country</th>
                  <th style={{ textAlign: 'right', width: 110 }}>Score</th>
                  <th>Top reason</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={item.brand.id}>
                    <td style={{ fontFamily: 'var(--font-heading)', opacity: 0.6 }}>#{idx + 1}</td>
                    <td>
                      <Link to={`/brands/${item.brand.id}`} style={{ fontWeight: 600 }}>
                        {item.brand.name}
                      </Link>
                    </td>
                    <td>{item.brand.category}</td>
                    <td>{item.brand.country || '—'}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-heading)', fontSize: 16, color: 'var(--color-accent-2-700)' }}>
                      {Math.round(item.score || 0)}
                      <span style={{ fontSize: 12, opacity: 0.5 }}>/100</span>
                    </td>
                    <td style={{ fontSize: 13, opacity: 0.8 }}>{item.top_reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ margin: 0, padding: 'var(--space-4) 0', fontSize: 14, opacity: 0.65 }}>
              {previewing ? 'Scoring…' : preview?.message || 'No brands to score yet.'}
            </p>
          )
        ) : (
          <p style={{ margin: 0, padding: 'var(--space-4) 0', fontSize: 14, opacity: 0.65 }}>
            {noPreviewNote}
          </p>
        )}
      </div>

      {confirmRevert && (
        <div className="dialog-backdrop" onClick={() => setConfirmRevert(false)}>
          <div className="dialog" role="alertdialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-title">Revert to momentum?</div>
            <div className="dialog-body">
              {hasConfig
                ? 'Remove the custom score? Movers reverts to the default momentum ranking.'
                : 'Set every weight back to zero? Movers stays on the default momentum ranking.'}
            </div>
            <div className="dialog-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmRevert(false)}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={handleRevert}>
                <RotateCcw size={15} strokeWidth={STROKE} /> Revert
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScoringTab;
